import { useCallback, useEffect, useState } from 'react';
import {
  createActivity,
  deleteActivity,
  extractActivitiesFromResponse,
  extractActivityFromResponse,
  fetchActivities,
  updateActivity,
} from '../api/activities';
import { getApiErrorMessage } from '../api/errors';
import AdminIconButton from './AdminIconButton';
import AdminModal from './AdminModal';
import AdminPagination from './AdminPagination';
import AdminTableLoader from './AdminTableLoader';
import { confirmDelete, showError, showSuccess } from './adminAlerts';
import { usePagination } from './usePagination';
import './adminShared.css';

function AdminActivity() {
  const [activities, setActivities] = useState([]);
  const [modalMode, setModalMode] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const loadActivities = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await fetchActivities();
      setActivities(extractActivitiesFromResponse(response));
    } catch (err) {
      showError(getApiErrorMessage(err, 'Could not load activities.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  const closeModal = () => {
    setModalMode(null);
    setEditingId(null);
    setName('');
    setDescription('');
  };

  const openAddModal = () => {
    setModalMode('add');
    setEditingId(null);
    setName('');
    setDescription('');
  };

  const openEditModal = (activity) => {
    setModalMode('edit');
    setEditingId(activity.id);
    setName(activity.name);
    setDescription(activity.description || '');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      if (modalMode === 'add') {
        const response = await createActivity({
          name: name.trim(),
          description: description.trim(),
        });
        const activity = extractActivityFromResponse(response);
        setActivities((prev) => [activity, ...prev]);
        showSuccess('Activity created.');
      } else {
        const response = await updateActivity(editingId, {
          name: name.trim(),
          description: description.trim(),
        });
        const updated = extractActivityFromResponse(response);
        setActivities((prev) =>
          prev.map((item) => (item.id === editingId ? { ...item, ...updated } : item))
        );
        showSuccess('Activity updated.');
      }
      closeModal();
    } catch (err) {
      showError(
        getApiErrorMessage(
          err,
          modalMode === 'add' ? 'Could not create activity.' : 'Could not update activity.'
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDelete({
      title: 'Delete activity?',
      text: 'This activity will be removed permanently.',
    });
    if (!confirmed) return;

    setBusyId(id);

    try {
      await deleteActivity(id);
      setActivities((prev) => prev.filter((item) => item.id !== id));
      if (editingId === id) closeModal();
      showSuccess('Activity deleted.');
    } catch (err) {
      showError(getApiErrorMessage(err, 'Could not delete activity.'));
    } finally {
      setBusyId(null);
    }
  };

  const {
    paginatedItems,
    page,
    setPage,
    totalPages,
    totalItems,
    rangeStart,
    rangeEnd,
  } = usePagination(activities);

  return (
    <div className="admin-page">
      <div className="admin-page__top">
        <div>
          <h1 className="admin-page__title">Activity</h1>
          <p className="admin-page__subtitle">Create and manage activities.</p>
        </div>
        <button type="button" className="admin-btn admin-btn--primary" onClick={openAddModal}>
          + Add
        </button>
      </div>

      {isLoading ? (
        <AdminTableLoader
          headers={['ID', 'Name', 'Description', 'Actions']}
          columns={['id', 'title', 'meta', 'actions']}
        />
      ) : activities.length === 0 ? (
        <p className="admin-panel__note">No activities yet.</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Description</th>
                <th className="admin-table__actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedItems.map((activity, index) => (
                <tr key={activity.id ?? `activity-${index}`}>
                  <td className="admin-table__id">{activity.id}</td>
                  <td>
                    <span className="admin-table__title">{activity.name}</span>
                  </td>
                  <td>
                    <span className="admin-table__meta">{activity.description}</span>
                  </td>
                  <td className="admin-table__actions">
                    <AdminIconButton
                      label="Edit activity"
                      onClick={() => openEditModal(activity)}
                      disabled={busyId === activity.id}
                    />
                    <AdminIconButton
                      label="Delete activity"
                      variant="danger"
                      onClick={() => handleDelete(activity.id)}
                      disabled={busyId === activity.id}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <AdminPagination
            page={page}
            totalPages={totalPages}
            totalItems={totalItems}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            onPageChange={setPage}
          />
        </div>
      )}

      {modalMode && (
        <AdminModal
          title={modalMode === 'add' ? 'Add activity' : 'Edit activity'}
          onClose={closeModal}
        >
          <form className="admin-form-grid" onSubmit={handleSubmit}>
            <label className="admin-field">
              <span className="admin-field__label">Name</span>
              <input
                className="admin-field__input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </label>
            <label className="admin-field">
              <span className="admin-field__label">Description</span>
              <textarea
                className="admin-field__input admin-field__textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                required
                disabled={isSubmitting}
              />
            </label>
            <div className="admin-actions">
              <button
                type="submit"
                className="admin-btn admin-btn--primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving…' : modalMode === 'add' ? 'Create' : 'Save'}
              </button>
              <button
                type="button"
                className="admin-btn admin-btn--secondary"
                onClick={closeModal}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </AdminModal>
      )}
    </div>
  );
}

export default AdminActivity;
